import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ParentIssueTypes } from '@risksmart-app/shared/forms/issues/types';
import type {
  IssueRegisterResponse,
  IssueRegisterResponseRow,
} from '@risksmart-app/trpc/src/types';
import type { GetIssuesQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

function mapTrpcIssueToGraphQL(
  issue: IssueRegisterResponseRow
): GetIssuesQuery['issue'][number] {
  return {
    ...issue,
    actions_aggregate: {
      aggregate: {
        count: issue.actions.length,
      },
    },
  };
}

/**
 * Maps TRPC issue data to match the GraphQL query structure
 */
export function mapTrpcIssuesToGraphQL(
  trpcData: IssueRegisterResponse | undefined
): GetIssuesQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    issue: trpcData.issue.map((issue) => mapTrpcIssueToGraphQL(issue)),
  };
}

export const useGetIssueRegister = (
  issueType: ParentIssueTypes,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.issue.register.queryOptions({
      issueType,
    }),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcIssuesToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
